/**
 * Stammetjes-zoeker. Haalt de aanbieders in een keer op uit de hub, zet een postcode
 * om in coordinaten (ook via de hub, die cachet), rekent de afstand zelf uit en tekent
 * een stippenkaart zonder kaartdienst van buiten.
 *
 * Alles staat in een custom element zodat het script pas iets doet als de sectie op de
 * pagina staat, en zodat de thema-editor de sectie kan herladen.
 */
class SamzwamStammetjes extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';

    try {
      this.s = JSON.parse(this.querySelector('[data-strings]').textContent);
    } catch (e) {
      // Zonder teksten valt er niets te tonen. Beter helemaal niets dan een
      // formulier vol lege labels waar niets op gebeurt.
      if (window.console) console.error('[samzwam-stammetjes] de teksten zijn niet te lezen', e);
      return;
    }
    this.api = (this.dataset.api || '').replace(/\/+$/, '');
    this.marktplaats = this.dataset.marktplaats || '';
    this.locale = (this.dataset.locale || 'nl').slice(0, 2);

    this.el = {
      soon: this.querySelector('[data-soon]'),
      tool: this.querySelector('[data-tool]'),
      form: this.querySelector('[data-form]'),
      land: this.querySelector('[data-land]'),
      postcode: this.querySelector('[data-postcode]'),
      radius: this.querySelector('[data-radius-select]'),
      mushroom: this.querySelector('[data-mushroom]'),
      fresh: this.querySelector('[data-fresh]'),
      locate: this.querySelector('[data-locate]'),
      status: this.querySelector('[data-status]'),
      list: this.querySelector('[data-list]'),
      map: this.querySelector('[data-map]'),
      mapCaption: this.querySelector('[data-map-caption]'),
      tplCard: this.querySelector('[data-tpl-card]'),
      dialog: this.querySelector('[data-dialog]'),
      contactForm: this.querySelector('[data-contact-form]'),
    };

    if (!this.poortOpen()) return;
    this.start();
  }

  /**
   * Het beheerdersslot. Liquid heeft al beslist voor ingelogde beheerders en de
   * thema-editor; hier alleen nog de sleutel in de URL, die we in de sessie onthouden
   * zodat een klik terug de zoeker niet weer dichtgooit.
   */
  poortOpen() {
    if (this.dataset.gate === 'open') return true;

    const sleutel = this.dataset.key || '';
    const opgegeven = new URLSearchParams(location.search).get('voorbeeld');
    let onthouden = null;
    try { onthouden = sessionStorage.getItem('samzwam-stammetjes-voorbeeld'); } catch (e) { /* prive-venster */ }

    if (sleutel && (opgegeven === sleutel || onthouden === sleutel)) {
      try { sessionStorage.setItem('samzwam-stammetjes-voorbeeld', sleutel); } catch (e) { /* niets */ }
      this.el.soon.hidden = true;
      this.el.tool.hidden = false;
      return true;
    }
    return false;
  }

  async start() {
    this.punt = null;
    this.toonAlles = false;
    this.el.status.textContent = this.s.loading_list;

    this.el.form.addEventListener('submit', (e) => { e.preventDefault(); this.zoekOpPostcode(); });
    this.el.land.addEventListener('change', () => this.landGewisseld());
    this.el.radius.addEventListener('change', () => this.punt && this.render());
    this.el.mushroom.addEventListener('change', () => this.punt && this.render());
    this.el.fresh.addEventListener('change', () => this.punt && this.render());
    if (this.el.locate) this.el.locate.addEventListener('click', () => this.zoekOpLocatie());
    this.koppelDialoog();
    this.landGewisseld();

    try {
      const antwoord = await haal(this.api + '/aanbieders');
      if (!antwoord.ok) throw new Error('api ' + antwoord.status);
      this.data = await antwoord.json();
      if (!this.data || !Array.isArray(this.data.aanbieders)) throw new Error('geen lijst');
    } catch (e) {
      this.el.status.textContent = this.s.api_down;
      // Zonder lijst valt er niets te zoeken. De knop uitzetten is eerlijker
      // dan hem laten staan en niets doen als de klant erop drukt.
      this.el.form.querySelectorAll('button, select, input').forEach((veld) => { veld.disabled = true; });
      return;
    }

    this.soorten = {};
    (this.data.boomsoorten || []).forEach((b) => { this.soorten[b.code] = b; });
    this.vulPaddenstoelen();
    this.el.status.textContent = '';
    this.tekenKaart([]);

    // De vorige zoekopdracht terughalen: wie terugkomt hoeft niet opnieuw te typen.
    try {
      const vorige = JSON.parse(localStorage.getItem('samzwam-stammetjes-zoek') || 'null');
      // Alleen terughalen als de klant nog niets zelf heeft ingetikt. Anders
      // bepaalt de netwerksnelheid welke van de twee zoekopdrachten wint, en
      // dat kan de oude uit de opslag zijn.
      if (vorige && vorige.postcode && this.el.postcode.value.trim() === '') {
        this.el.land.value = vorige.land || 'NL';
        this.el.postcode.value = vorige.postcode;
        this.landGewisseld();
        this.zoekOpPostcode();
      }
    } catch (e) { /* geen opslag, geen probleem */ }
  }

  landGewisseld() {
    const be = this.el.land.value === 'BE';
    this.el.postcode.placeholder = be ? this.el.postcode.dataset.phBe : this.el.postcode.dataset.phNl;
    this.el.postcode.inputMode = be ? 'numeric' : 'text';
  }

  vulPaddenstoelen() {
    const paddenstoelen = this.data.paddenstoelen || {};
    Object.keys(paddenstoelen).forEach((code) => {
      const optie = document.createElement('option');
      optie.value = code;
      optie.textContent = paddenstoelen[code];
      this.el.mushroom.appendChild(optie);
    });
  }

  async zoekOpPostcode() {
    if (!this.data) return;
    const land = this.el.land.value;
    const ruw = this.el.postcode.value.trim();
    const geldig = land === 'BE' ? /^[1-9]\d{3}$/.test(ruw.replace(/\s+/g, '')) : /^[1-9]\d{3}\s*[a-zA-Z]{2}$/.test(ruw);
    if (!geldig) {
      this.melding(land === 'BE' ? this.s.invalid_postcode_be : this.s.invalid_postcode_nl);
      this.el.postcode.focus();
      return;
    }

    this.melding(this.s.loading);
    let antwoord;
    try {
      const url = this.api + '/geocode?land=' + encodeURIComponent(land) + '&postcode=' + encodeURIComponent(ruw.replace(/\s+/g, '').toUpperCase());
      antwoord = await haal(url);
    } catch (e) {
      this.melding(this.s.service_down);
      return;
    }
    if (antwoord.status === 404) { this.melding(this.s.unknown_postcode); return; }
    if (antwoord.status === 422) { this.melding(land === 'BE' ? this.s.invalid_postcode_be : this.s.invalid_postcode_nl); return; }
    if (!antwoord.ok) { this.melding(this.s.service_down); return; }

    const punt = await antwoord.json().catch(() => null);
    // Een antwoord met een 200 maar zonder bruikbare getallen mag niet
    // doorglippen: dan worden alle afstanden NaN, valt iedereen buiten de
    // straal en leest de klant "niemand gevonden in de buurt" terwijl er in
    // werkelijkheid iets stuk is. Bovendien gaat de kaart er kapot van.
    if (!punt || !Number.isFinite(Number(punt.lat)) || !Number.isFinite(Number(punt.lon))) {
      this.melding(this.s.service_down);
      return;
    }
    this.punt = { lat: Number(punt.lat), lon: Number(punt.lon), naam: punt.naam || ruw };
    try { localStorage.setItem('samzwam-stammetjes-zoek', JSON.stringify({ land: land, postcode: ruw })); } catch (e) { /* niets */ }
    this.toonAlles = false;
    this.render();
  }

  zoekOpLocatie() {
    if (!this.data || !navigator.geolocation) return;
    this.melding(this.s.loading);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.punt = { lat: pos.coords.latitude, lon: pos.coords.longitude, naam: this.s.your_location };
        this.toonAlles = false;
        this.render();
      },
      () => this.melding(this.s.locate_denied),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 },
    );
  }

  melding(tekst) {
    this.el.status.textContent = tekst || '';
  }

  /**
   * Welke boomsoorten passen bij de gekozen paddenstoel. Leeg betekent: geen filter.
   */
  passendeSoorten() {
    const paddenstoel = this.el.mushroom.value;
    if (!paddenstoel) return null;
    return Object.values(this.soorten).filter((b) => (b.paddenstoelen || []).includes(paddenstoel)).map((b) => b.code);
  }

  treffers() {
    const straal = Number(this.el.radius.value);
    const passend = this.passendeSoorten();
    const alleenVers = this.el.fresh.checked;

    return (this.data.aanbieders || [])
      .map((a) => ({ a: a, km: afstand(this.punt.lat, this.punt.lon, a.lat, a.lon) }))
      .filter((r) => r.km <= straal)
      .filter((r) => !alleenVers || (r.a.aanbod || []).some((o) => o.vers === true))
      .map((r) => {
        // Zonder opgave van boomsoorten weten we het niet; die blijven staan maar
        // komen na de bedrijven die de gevraagde soort echt noemen.
        const soorten = r.a.boomsoorten || [];
        r.score = !passend ? 2 : (soorten.length === 0 ? 1 : (soorten.some((c) => passend.includes(c)) ? 2 : 0));
        return r;
      })
      .filter((r) => r.score > 0)
      .sort((x, y) => (y.score - x.score) || (x.km - y.km));
  }

  render() {
    if (!this.punt) return;
    const lijst = this.el.list;
    lijst.innerHTML = '';
    const straal = Number(this.el.radius.value);
    const alles = this.treffers();
    const gefilterd = this.el.mushroom.value || this.el.fresh.checked;

    if (!alles.length) {
      // Ook nul treffers is een antwoord, en het meldgebied is de enige plek
      // waar een schermlezer het hoort. Met een lege melding lijkt de knop
      // kapot.
      this.melding(vul(gefilterd ? this.s.no_results_filter : this.s.no_results, { radius: straal }));
      lijst.appendChild(this.leegBlok(straal, gefilterd));
      this.tekenKaart([]);
      return;
    }

    const max = this.toonAlles ? alles.length : 12;
    const zichtbaar = alles.slice(0, max);
    this.alleTreffers = alles;
    this.melding(vul(this.s.results_count, { count: alles.length, radius: straal, place: this.punt.naam })
      + (alles.length > zichtbaar.length ? ' ' + vul(this.s.results_more, { n: zichtbaar.length }) : ''));

    const passend = this.passendeSoorten();
    zichtbaar.forEach((r) => lijst.appendChild(this.kaartje(r.a, r.km, passend)));

    if (alles.length > zichtbaar.length) {
      const meer = document.createElement('button');
      meer.type = 'button';
      meer.className = 'button button--secondary samzwam-stammetjes__more';
      meer.textContent = this.s.show_more;
      meer.addEventListener('click', () => { this.toonAlles = true; this.render(); });
      lijst.appendChild(meer);
    }

    // Alleen de getoonde treffers krijgen een rode stip. Een stip van nummer 30
    // terwijl er twaalf kaartjes staan is een dode klik: er is niets om naartoe
    // te springen.
    this.tekenKaart(zichtbaar.map((r) => r.a.id));
  }

  leegBlok(straal, gefilterd) {
    const blok = document.createElement('div');
    blok.className = 'samzwam-stammetjes__empty';
    const p = document.createElement('p');
    p.className = 'h4';
    p.textContent = vul(gefilterd ? this.s.no_results_filter : this.s.no_results, { radius: straal });
    blok.appendChild(p);

    const acties = document.createElement('div');
    acties.className = 'samzwam-stammetjes__empty-actions';
    const volgende = [25, 50, 100].find((r) => r > straal);
    if (volgende) {
      const knop = document.createElement('button');
      knop.type = 'button';
      knop.className = 'button';
      knop.textContent = vul(this.s.widen, { radius: volgende });
      knop.addEventListener('click', () => { this.el.radius.value = String(volgende); this.render(); });
      acties.appendChild(knop);
    }
    if (this.marktplaats && this.el.land.value === 'NL') {
      const hint = document.createElement('p');
      hint.textContent = this.s.marktplaats_hint;
      blok.appendChild(hint);
      const link = document.createElement('a');
      link.className = 'button button--secondary';
      link.href = this.marktplaats;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = this.s.marktplaats_button;
      acties.appendChild(link);
    }
    blok.appendChild(acties);
    return blok;
  }

  kaartje(a, km, passend) {
    const node = this.el.tplCard.content.firstElementChild.cloneNode(true);
    node.dataset.id = a.id;
    if (a.dummy) node.classList.add('samzwam-stammetjes__card--test');
    node.querySelector('[data-name]').textContent = a.naam;

    // Van een bedrijf waarvan we alleen de plaatsnaam kennen, staat het punt in
    // het midden van het dorp. Dan zeggen we "ongeveer", want anders belooft de
    // pagina een nauwkeurigheid die er niet is.
    const afstandVak = node.querySelector('[data-distance]');
    const getal = km.toFixed(1).replace('.', this.locale === 'en' ? '.' : ',') + ' km';
    afstandVak.textContent = a.punt_bij_benadering ? vul(this.s.about_km, { km: getal }) : getal;
    afstandVak.setAttribute('aria-label', vul(this.s.distance_label, { km: afstandVak.textContent }));
    if (a.punt_bij_benadering) {
      afstandVak.title = this.s.approximate;
      afstandVak.classList.add('is-ongeveer');
    }

    node.querySelector('[data-place]').textContent = [a.plaats, a.provincie].filter(Boolean).join(', ');

    const badges = node.querySelector('[data-badges]');
    const heeftVers = (a.aanbod || []).some((o) => o.vers === true);
    if (a.dummy) badges.appendChild(badge(this.s.badge_test, 'test'));
    if (heeftVers) badges.appendChild(badge(this.s.badge_fresh, 'fresh'));
    (a.certificaten || []).forEach((c) => badges.appendChild(badge(c, 'cert')));
    badges.appendChild(badge(a.hele_jaar ? this.s.badge_year_round : this.s.badge_season, a.hele_jaar ? 'year' : 'season'));

    if (a.omschrijving) {
      const desc = node.querySelector('[data-desc]');
      desc.textContent = a.omschrijving;
      desc.hidden = false;
    }

    const aanbod = (a.aanbod || []);
    if (a.deelname === 'aanbod' && aanbod.length) {
      const ul = node.querySelector('[data-offer-list]');
      aanbod.forEach((o) => {
        const li = document.createElement('li');
        if (o.vers === true) li.classList.add('is-fresh');
        const delen = [];
        const naam = this.soorten[o.boomsoort] ? this.soorten[o.boomsoort].naam : o.boomsoort;
        const sterk = document.createElement('strong');
        sterk.textContent = naam;
        li.appendChild(sterk);
        if (o.gekapt_op) delen.push(o.vers === false ? vul(this.s.offer_old, { days: this.data.vers_dagen || 90 }) : vul(this.s.offer_cut, { when: this.wanneer(o.gekapt_op) }));
        if (o.aantal) delen.push(vul(this.s.offer_pieces, { n: o.aantal }));
        if (o.diameter) delen.push(o.diameter);
        if (o.lengte_cm) delen.push(o.lengte_cm + ' cm');
        if (o.opmerking) delen.push(o.opmerking);
        li.appendChild(document.createTextNode(delen.length ? ' · ' + delen.join(' · ') : ''));
        ul.appendChild(li);
      });
      node.querySelector('[data-offer]').hidden = false;
    } else if (a.deelname !== 'aanbod') {
      const p = node.querySelector('[data-desc]');
      p.textContent = (p.textContent ? p.textContent + ' ' : '') + this.s.badge_listing;
      p.hidden = false;
    }

    const soorten = a.boomsoorten || [];
    if (soorten.length) {
      const wrap = node.querySelector('[data-species-list]');
      soorten.forEach((code) => {
        const chip = document.createElement('span');
        chip.className = 'samzwam-stammetjes__chip' + (passend && passend.includes(code) ? ' is-match' : '');
        chip.textContent = this.soorten[code] ? this.soorten[code].naam : code;
        wrap.appendChild(chip);
      });
      node.querySelector('[data-species]').hidden = false;
    }

    const contact = node.querySelector('[data-contact]');
    const c = a.contact || {};
    const site = veiligeUrl(c.website);
    if (a.direct_contact && (c.telefoon || c.email || site)) {
      if (c.telefoon) contact.appendChild(knop(this.s.contact_call, 'tel:' + c.telefoon.replace(/[^\d+]/g, ''), c.telefoon));
      // Het mailadres door encodeURIComponent: anders kan een adres met een
      // vraagteken erin het onderwerp of de tekst van de mail voorschrijven.
      if (c.email) contact.appendChild(knop(this.s.contact_mail, 'mailto:' + encodeURIComponent(c.email), c.email));
      if (site) contact.appendChild(knop(this.s.contact_site, site, site.replace(/^https?:\/\//, '').replace(/\/$/, ''), true));
    } else {
      const via = document.createElement('button');
      via.type = 'button';
      via.className = 'button samzwam-stammetjes__via';
      via.textContent = this.s.contact_via;
      via.addEventListener('click', () => this.openDialoog(a));
      contact.appendChild(via);
    }
    return node;
  }

  wanneer(datum) {
    const dagen = Math.max(0, Math.round((Date.now() - new Date(datum + 'T12:00:00').getTime()) / 86400000));
    if (dagen < 7) return this.s.this_week;
    if (dagen < 60) {
      const weken = Math.max(1, Math.round(dagen / 7));
      return weken === 1 ? this.s.week_ago : vul(this.s.weeks_ago, { n: weken });
    }
    const maanden = Math.max(2, Math.round(dagen / 30));
    return vul(this.s.months_ago, { n: maanden });
  }

  koppelDialoog() {
    const d = this.el.dialog;
    if (!d || !this.el.contactForm) return;
    d.querySelector('[data-dialog-close]').addEventListener('click', () => this.sluitDialoog());
    d.addEventListener('click', (e) => { if (e.target === d) this.sluitDialoog(); });
    // Escape sluit een dialog van zichzelf; dit zet de focus ook terug.
    d.addEventListener('close', () => {
      if (this.opener && typeof this.opener.focus === 'function') {
        this.opener.focus();
        this.opener = null;
      }
    });
    this.el.contactForm.addEventListener('submit', (e) => { e.preventDefault(); this.verstuurContact(); });
  }

  openDialoog(a) {
    const d = this.el.dialog;
    if (!d || typeof d.showModal !== 'function') {
      location.href = 'mailto:stammetjes@samzwam.nl?subject=' + encodeURIComponent('Stammetjes: ' + a.naam);
      return;
    }
    this.el.contactForm.reset();
    this.el.contactForm.querySelector('[name=aanbieder]').value = a.id;
    d.querySelector('[data-dialog-title]').textContent = a.naam;
    d.querySelector('[data-dialog-status]').textContent = '';
    d.querySelector('[data-dialog-status]').className = 'samzwam-stammetjes__status';
    const pc = this.el.contactForm.querySelector('[name=postcode]');
    if (pc && !pc.value && this.el.postcode.value) pc.value = this.el.postcode.value;

    // Onthouden wie het venster opende, zodat de focus daar naar terug kan.
    // showModal() doet dat zelf, maar niet in elke browser, en na een gelukte
    // verzending sluiten wij het venster met een timer.
    this.opener = document.activeElement;
    d.showModal();
    this.el.contactForm.querySelector('[name=naam]').focus();
  }

  sluitDialoog() {
    this.el.dialog.close();
    if (this.opener && typeof this.opener.focus === 'function') {
      this.opener.focus();
      this.opener = null;
    }
  }

  async verstuurContact() {
    const form = this.el.contactForm;
    const status = this.el.dialog.querySelector('[data-dialog-status]');
    const knop = form.querySelector('button[type=submit]');
    const gegevens = {};
    new FormData(form).forEach((v, k) => { gegevens[k] = v; });

    let gelukt = false;
    knop.disabled = true;
    status.textContent = this.s.form_sending;
    try {
      const antwoord = await haal(this.api + '/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(gegevens),
      });
      if (!antwoord.ok) throw new Error('contact ' + antwoord.status);
      gelukt = true;
      const uitslag = await antwoord.json().catch(() => ({}));
      // Zolang de hub niet echt mailt, komt het bericht bij ons terecht en
      // sturen wij het door. Dat zeggen we dan ook, in plaats van te doen
      // alsof de aanbieder het al heeft.
      status.textContent = uitslag.doorgestuurd ? this.s.form_sent : this.s.form_received;
      status.classList.add('is-ok');
      form.querySelectorAll('input, textarea').forEach((veld) => { if (veld.type !== 'hidden') veld.disabled = true; });
      setTimeout(() => {
        form.querySelectorAll('input, textarea').forEach((veld) => { veld.disabled = false; });
        knop.disabled = false;
        this.sluitDialoog();
      }, 2500);
    } catch (e) {
      status.textContent = this.s.form_error;
      status.classList.add('is-error');
    } finally {
      // Na een gelukte verzending blijft de knop uit tot het venster sluit.
      // Anders kan de klant in die 2,5 seconde nog een keer drukken, en omdat
      // de velden dan uit staan stuurt het formulier een leeg bericht: een
      // rommelregel in de hub en een foutmelding over de gelukte melding heen.
      knop.disabled = gelukt;
    }
  }

  /**
   * Kaart met de omtrek van het land en daarop elke aanbieder als stip.
   *
   * Het idee was eerst dat de vorm van het land zichzelf zou tekenen zodra er genoeg
   * stippen staan. Dat klopt niet: de kaart toont alleen de aanbieders die meedoen, en
   * dat worden er tientallen, geen duizenden. Dan zie je een wit vlak met wat stipjes en
   * weet je niet waar je kijkt. Dus ligt er nu een echte omtrek onder, uit de publieke
   * grensdata van Natural Earth, vereenvoudigd tot 367 punten.
   *
   * Treffers kleuren rood, de eigen postcode krijgt een ring.
   */
  tekenKaart(trefferIds) {
    const svg = this.el.map;
    if (!svg || !this.data) return;
    const alle = (this.data.aanbieders || []).filter((a) => a.lat && a.lon);
    if (!alle.length) { svg.innerHTML = ''; return; }

    const midLat = (Math.min(...alle.map((a) => a.lat)) + Math.max(...alle.map((a) => a.lat))) / 2;
    // Lengtegraden liggen op deze breedte dichter bij elkaar dan breedtegraden; zonder
    // deze correctie wordt het land te breed uitgerekt.
    const kromming = Math.cos(midLat * Math.PI / 180);
    const naar = (lat, lon) => ({ x: lon * kromming * 100, y: -lat * 100 });
    const punten = alle.map((a) => Object.assign({ a: a }, naar(a.lat, a.lon)));
    const extra = this.punt ? [naar(this.punt.lat, this.punt.lon)] : [];
    // Straal in dezelfde eenheden als de punten: 1 breedtegraad is ongeveer 111 km.
    const straalEenheden = this.punt ? (Number(this.el.radius.value) / 111) * 100 : 0;

    // Alleen de landen tekenen waar ook echt iemand staat. Anders krijgt een Belgische
    // bezoeker half Nederland in beeld en zijn eigen land als postzegel.
    const landen = new Set(alle.map((a) => a.land));
    const vormen = [];
    Object.keys(LANDVORM).forEach((code) => {
      if (! landen.has(code)) return;
      // Per land een lijst ringen: het vasteland, de eilanden, en het IJsselmeer.
      // LANDVORM staat plat in het bestand (lon, lat, lon, lat, ...); dat scheelt
      // bijna een kilobyte aan haakjes tegenover een lijst van paren.
      vormen.push(LANDVORM[code].map((deel) => {
        const ring = [];
        for (let i = 0; i < deel.length; i += 2) ring.push(naar(deel[i + 1], deel[i]));
        return ring;
      }));
    });

    const xs = punten.map((p) => p.x).concat(extra.map((p) => p.x));
    const ys = punten.map((p) => p.y).concat(extra.map((p) => p.y));
    // De omtrek meenemen in het beeld: de kaart laat zo altijd het hele land zien, ook
    // als alle aanbieders toevallig in een hoek zitten.
    vormen.forEach((land) => land.forEach((ring) => ring.forEach((p) => { xs.push(p.x); ys.push(p.y); })));
    // De straalcirkel hoort binnen het beeld te vallen, anders knipt de SVG hem
    // weg en blijft er een vlak zonder rand over.
    if (this.punt) {
      xs.push(extra[0].x - straalEenheden, extra[0].x + straalEenheden);
      ys.push(extra[0].y - straalEenheden, extra[0].y + straalEenheden);
    }
    const rand = 8;
    const minX = Math.min(...xs) - rand, maxX = Math.max(...xs) + rand;
    const minY = Math.min(...ys) - rand, maxY = Math.max(...ys) + rand;
    svg.setAttribute('viewBox', [minX.toFixed(2), minY.toFixed(2), (maxX - minX).toFixed(2), (maxY - minY).toFixed(2)].join(' '));

    const hits = new Set(trefferIds);
    const schaal = Math.max(maxX - minX, maxY - minY) / 100;
    let html = '';
    vormen.forEach((land) => {
      // Alle ringen van een land in een pad, met fill-rule evenodd. Daardoor wordt
      // het IJsselmeer, dat binnen het vasteland ligt, vanzelf een gat, terwijl de
      // Wadden- en Zeeuwse eilanden gewoon gevuld blijven.
      //
      // non-scaling-stroke: een haarlijn blijft een haarlijn, hoe ver de kaart ook
      // inzoomt. Bij de cirkels hieronder kan dat niet, want die moeten juist wel
      // meeschalen met hun eigen vorm.
      const d = land
        .map((ring) => ring.map((p, i) => (i === 0 ? 'M' : 'L') + p.x.toFixed(2) + ' ' + p.y.toFixed(2)).join('') + 'Z')
        .join('');
      html += '<path class="samzwam-stammetjes__land" fill-rule="evenodd" vector-effect="non-scaling-stroke" d="' + d + '"/>';
    });
    if (this.punt) {
      const p = extra[0];
      // De lijndikte meeschalen met het beeld: een vaste dikte uit de CSS is
      // bij een landelijk beeld een onzichtbare haarlijn en bij een uitsnede
      // van een dorp een balk over de halve kaart.
      html += '<circle class="samzwam-stammetjes__radius" cx="' + p.x.toFixed(2) + '" cy="' + p.y.toFixed(2)
        + '" r="' + straalEenheden.toFixed(2) + '" stroke-width="' + (0.35 * schaal).toFixed(3)
        + '" stroke-dasharray="' + (1.2 * schaal).toFixed(3) + ' ' + (1.2 * schaal).toFixed(3) + '"/>';
    }
    punten.filter((p) => !hits.has(p.a.id)).forEach((p) => {
      html += '<circle class="samzwam-stammetjes__dot" cx="' + p.x.toFixed(2) + '" cy="' + p.y.toFixed(2) + '" r="' + (1.1 * schaal).toFixed(2) + '"><title>' + esc(p.a.naam) + '</title></circle>';
    });
    punten.filter((p) => hits.has(p.a.id)).forEach((p) => {
      // Ook de id door esc(): dat is nu een getal uit de hub, maar dit is de
      // enige waarde in de hele kaart die zonder escape in een attribuut zou
      // staan, en in SVG zijn er genoeg manieren om daaruit te breken.
      html += '<circle class="samzwam-stammetjes__dot is-hit" data-id="' + esc(p.a.id) + '" cx="' + p.x.toFixed(2) + '" cy="' + p.y.toFixed(2) + '" r="' + (1.9 * schaal).toFixed(2) + '"><title>' + esc(p.a.naam) + '</title></circle>';
    });
    if (this.punt) {
      const p = extra[0];
      html += '<circle class="samzwam-stammetjes__you" cx="' + p.x.toFixed(2) + '" cy="' + p.y.toFixed(2)
        + '" r="' + (2.4 * schaal).toFixed(2) + '" stroke-width="' + (0.8 * schaal).toFixed(3)
        + '"><title>' + esc(this.punt.naam) + '</title></circle>';
    }
    svg.innerHTML = html;

    if (this.el.mapCaption) {
      this.el.mapCaption.textContent = hits.size
        ? vul(this.s.map_caption_hits, { count: alle.length, hits: hits.size })
        : vul(this.s.map_caption, { count: alle.length });
    }

    if (this.el.mapCaption) {
      // De kaart is een plaatje voor een schermlezer; het label moet dus zeggen
      // wat er te zien is, niet alleen dat er een kaart staat.
      svg.setAttribute('aria-label', this.el.mapCaption.textContent);
    }

    // Klik op een rode stip: naar het kaartje scrollen. Dit is een extraatje
    // voor de muis; wie met het toetsenbord werkt heeft de lijst zelf, die
    // dezelfde bedrijven in dezelfde volgorde toont.
    //
    // Niet per stip een luisteraar, maar een luisteraar op de kaart die de
    // dichtstbijzijnde stip zoekt. Op een telefoon is de kaart 240 pixels breed
    // en is een stip nog geen 8 pixels: precies raken lukt daar niet. Zo telt
    // alles binnen een halve duimbreedte als een treffer, en bij twee stippen
    // naast elkaar wint de dichtstbijzijnde in plaats van de bovenste.
    //
    // De luisteraar hangt aan de kaart zelf en die blijft staan, terwijl de
    // stippen bij elke zoekopdracht opnieuw getekend worden. Dus een keer
    // aanhangen, anders springt de pagina na vijf zoekopdrachten vijf keer.
    if (svg.dataset.klikKlaar) return;
    svg.dataset.klikKlaar = '1';

    svg.addEventListener('click', (gebeurtenis) => {
      const stip = this.stipBij(svg, gebeurtenis.clientX, gebeurtenis.clientY);
      if (!stip) return;
      const kaart = this.el.list.querySelector('[data-id="' + stip.dataset.id + '"]');
      if (!kaart) return;
      // In de luisteraar uitlezen en niet daarbuiten: wie halverwege in zijn
      // toestel "beweging beperken" aanzet, merkt dat meteen.
      const zachtScrollen = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      kaart.scrollIntoView({ behavior: zachtScrollen ? 'smooth' : 'auto', block: 'center' });
      kaart.classList.add('is-flash');
      setTimeout(() => kaart.classList.remove('is-flash'), 1600);
    });
  }

  /**
   * De rode stip die het dichtst bij een aanraking ligt, of null als er geen
   * enkele binnen bereik ligt. MARGE_PX is de straal in schermpixels: samen met
   * de stip zelf komt dat op ruim de 44 pixels die een raakvlak hoort te hebben.
   */
  stipBij(svg, x, y) {
    const MARGE_PX = 22;
    let beste = null;
    let besteAfstand = Infinity;
    svg.querySelectorAll('.is-hit').forEach((stip) => {
      const vlak = stip.getBoundingClientRect();
      const dx = x - (vlak.left + vlak.width / 2);
      const dy = y - (vlak.top + vlak.height / 2);
      const afstand = Math.sqrt(dx * dx + dy * dy);
      // De stip telt vanaf zijn rand, niet vanaf zijn midden: een dikke stip
      // op een uitvergrote kaart hoort niet nog eens 22 pixels extra te vangen.
      if (afstand - vlak.width / 2 < MARGE_PX && afstand < besteAfstand) {
        besteAfstand = afstand;
        beste = stip;
      }
    });

    return beste;
  }
}

function afstand(lat1, lon1, lat2, lon2) {
  const R = 6371, r = Math.PI / 180;
  const dLat = (lat2 - lat1) * r, dLon = (lon2 - lon1) * r;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/**
 * Haalt een adres op met een tijdslimiet. Zonder limiet blijft een hangende
 * verbinding (een deploy, een firewall die pakketten laat vallen) eeuwig staan
 * en blijft de pagina op "laden" hangen: dat is erger dan een nette foutmelding.
 */
function haal(url, opties) {
  const stopper = new AbortController();
  const klok = setTimeout(() => stopper.abort(), 12000);

  return fetch(url, Object.assign({ headers: { Accept: 'application/json' }, signal: stopper.signal }, opties || {}))
    .finally(() => clearTimeout(klok));
}

function vul(sjabloon, waarden) {
  return String(sjabloon || '').replace(/\[(\w+)\]/g, (m, k) => {
    if (!(k in waarden) || waarden[k] === undefined || waarden[k] === null) {
      // Een plaatshouder die niet ingevuld raakt is een vertaalfout. Laat hem
      // staan zoals hij is en zeg het in de console, anders leest de klant
      // "binnen km" of "undefined" en weet niemand waarom.
      if (window.console) console.warn('[samzwam-stammetjes] geen waarde voor plaatshouder', m, 'in', sjabloon);
      return m;
    }
    return String(waarden[k]);
  });
}

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/**
 * Alleen http en https mogen in een href. De server let hier ook al op, maar
 * een adres kan uit een oudere rij in de database komen, en een `javascript:`
 * in een link op samzwam.nl draait in de winkel zelf, met de sessie van de
 * klant erbij. Twee sloten op dezelfde deur.
 */
function veiligeUrl(ruw) {
  const tekst = String(ruw == null ? '' : ruw).trim();
  if (!tekst) return null;
  // Zonder schema wordt het anders een pad op samzwam.nl in plaats van een
  // verwijzing naar het bedrijf.
  const heel = /^[a-z][a-z0-9+.-]*:/i.test(tekst) ? tekst : 'https://' + tekst;
  try {
    const url = new URL(heel);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch (e) {
    return null;
  }
}

function badge(tekst, soort) {
  const b = document.createElement('span');
  b.className = 'samzwam-stammetjes__badge samzwam-stammetjes__badge--' + soort;
  b.textContent = tekst;
  return b;
}

function knop(label, href, detail, extern) {
  const a = document.createElement('a');
  a.className = 'samzwam-stammetjes__contact-link';
  a.href = href;
  if (extern) { a.target = '_blank'; a.rel = 'noopener'; }
  const l = document.createElement('span');
  l.className = 'samzwam-stammetjes__contact-label';
  l.textContent = label;
  const d = document.createElement('span');
  d.className = 'samzwam-stammetjes__contact-detail';
  d.textContent = detail;
  a.appendChild(l);
  a.appendChild(d);
  return a;
}

if (!customElements.get('samzwam-stammetjes')) {
  customElements.define('samzwam-stammetjes', SamzwamStammetjes);
}

/**
 * De omtrek van Nederland en Belgie, plat opgeslagen als lon, lat, lon, lat.
 *
 * Bron: Natural Earth 1:50m admin-0 (publiek domein). Alleen het Europese deel, dus
 * zonder Bonaire, Saba en Sint Eustatius, en vereenvoudigd met Douglas-Peucker tot
 * 0,004 graden. Dat is op de grootste kaart die dit onderdeel ooit krijgt nog altijd
 * minder dan een pixel, en het scheelt het verschil tussen 4 kB en een halve megabyte.
 *
 * Opnieuw maken kan met `harvest/landvorm.py` in het stammetjes-project.
 */
const LANDVORM = {
  NL: [[5.994,50.750,5.747,50.760,5.694,50.775,5.639,50.844,5.648,50.867,5.737,50.932,5.750,50.950,5.741,50.960,5.750,50.989,5.818,51.086,5.827,51.126,5.796,51.153,5.752,51.169,5.609,51.198,5.540,51.239,5.509,51.275,5.477,51.285,5.430,51.273,5.311,51.260,5.214,51.279,5.100,51.346,5.059,51.453,5.031,51.469,4.944,51.408,4.848,51.403,4.821,51.412,4.811,51.453,4.784,51.477,4.756,51.491,4.634,51.422,4.589,51.422,4.532,51.449,4.503,51.475,4.441,51.460,4.385,51.428,4.404,51.367,4.374,51.356,4.304,51.362,4.226,51.386,4.139,51.402,4.007,51.443,3.822,51.409,3.694,51.450,3.587,51.454,3.521,51.486,3.449,51.541,3.500,51.577,3.549,51.589,3.744,51.596,3.886,51.574,4.141,51.456,4.206,51.457,4.274,51.472,4.239,51.504,4.080,51.551,4.005,51.596,4.183,51.610,4.158,51.633,4.135,51.673,3.947,51.811,3.979,51.848,4.026,51.928,4.085,51.994,4.132,52.012,4.209,52.059,4.376,52.197,4.483,52.309,4.562,52.443,4.678,52.810,4.713,52.872,4.769,52.941,4.839,52.928,4.888,52.908,5.061,52.961,5.358,53.096,5.446,53.214,5.532,53.269,5.874,53.375,6.062,53.407,6.353,53.415,6.564,53.434,6.816,53.441,6.912,53.375,6.968,53.327,7.058,53.301,7.197,53.282,7.189,53.187,7.190,53.000,7.179,52.966,7.117,52.887,7.051,52.745,7.033,52.651,7.013,52.634,6.748,52.634,6.711,52.618,6.705,52.598,6.719,52.574,6.712,52.550,6.692,52.530,6.703,52.499,6.749,52.464,6.833,52.442,6.968,52.444,7.002,52.419,7.035,52.380,7.033,52.331,7.020,52.266,6.977,52.206,6.855,52.136,6.800,52.111,6.749,52.099,6.725,52.080,6.713,52.057,6.716,52.036,6.802,51.980,6.800,51.967,6.775,51.938,6.742,51.911,6.518,51.854,6.425,51.858,6.356,51.825,6.297,51.851,6.167,51.881,6.117,51.870,6.090,51.854,6.008,51.834,5.949,51.803,5.949,51.762,6.053,51.658,6.089,51.638,6.091,51.599,6.193,51.489,6.199,51.450,6.193,51.411,6.166,51.355,6.076,51.224,6.075,51.199,6.082,51.180,6.137,51.165,6.130,51.147,5.939,51.041,5.868,51.045,5.858,51.030,5.867,51.006,5.895,50.984,5.955,50.973,6.007,50.950,6.048,50.905,5.994,50.750],[4.226,51.386,4.211,51.349,4.173,51.307,4.040,51.247,3.902,51.208,3.831,51.213,3.782,51.233,3.756,51.255,3.682,51.276,3.580,51.286,3.472,51.242,3.433,51.246,3.403,51.264,3.380,51.291,3.350,51.378,3.426,51.394,3.589,51.399,3.717,51.369,3.883,51.354,4.011,51.396,4.112,51.361,4.226,51.386],[3.949,51.739,4.047,51.685,4.068,51.667,4.075,51.649,3.951,51.627,3.819,51.694,3.732,51.678,3.699,51.710,3.699,51.730,3.789,51.746,3.949,51.739],[5.929,53.459,5.732,53.443,5.665,53.455,5.654,53.467,5.708,53.473,5.876,53.475,5.928,53.465,5.929,53.459],[5.326,53.386,5.233,53.378,5.190,53.392,5.415,53.431,5.557,53.444,5.583,53.438,5.326,53.386],[4.886,53.071,4.787,53.000,4.727,53.020,4.709,53.036,4.740,53.091,4.886,53.183,4.886,53.071],[6.333,53.511,6.193,53.477,6.159,53.484,6.168,53.494,6.291,53.515,6.333,53.511],[6.735,53.583,6.642,53.579,6.669,53.606,6.755,53.625,6.801,53.625,6.735,53.583],[5.109,53.308,4.924,53.235,4.908,53.246,5.027,53.310,5.109,53.308],[5.867,52.563,5.870,52.485,5.795,52.428,5.495,52.252,5.040,52.344,5.014,52.364,5.036,52.405,5.062,52.431,5.069,52.451,5.070,52.520,5.060,52.566,5.066,52.624,5.138,52.627,5.242,52.670,5.247,52.730,5.165,52.786,5.118,52.836,5.094,52.886,5.068,52.923,5.269,53.027,5.377,53.065,5.398,53.042,5.420,52.874,5.455,52.857,5.547,52.846,5.657,52.851,5.667,52.817,5.625,52.782,5.618,52.758,5.613,52.684,5.619,52.658,5.653,52.630,5.835,52.609,5.867,52.563]],
  BE: [[4.226,51.386,4.304,51.362,4.374,51.356,4.404,51.367,4.385,51.428,4.441,51.460,4.503,51.475,4.532,51.449,4.589,51.422,4.634,51.422,4.756,51.491,4.784,51.477,4.811,51.453,4.821,51.412,4.848,51.403,4.944,51.408,5.031,51.469,5.059,51.453,5.100,51.346,5.214,51.279,5.311,51.260,5.430,51.273,5.477,51.285,5.509,51.275,5.540,51.239,5.609,51.198,5.752,51.169,5.796,51.153,5.827,51.126,5.818,51.086,5.750,50.989,5.741,50.960,5.750,50.950,5.737,50.932,5.648,50.867,5.639,50.844,5.694,50.775,5.747,50.760,5.994,50.750,6.006,50.732,6.119,50.679,6.154,50.637,6.236,50.597,6.168,50.545,6.179,50.523,6.203,50.499,6.295,50.485,6.341,50.452,6.344,50.400,6.364,50.316,6.175,50.233,6.121,50.139,6.117,50.121,6.089,50.155,6.055,50.154,5.976,50.167,5.867,50.083,5.788,49.961,5.744,49.920,5.735,49.876,5.741,49.857,5.726,49.833,5.725,49.808,5.788,49.759,5.804,49.732,5.880,49.645,5.815,49.554,5.790,49.538,5.710,49.539,5.610,49.528,5.542,49.511,5.507,49.511,5.435,49.554,5.354,49.620,5.302,49.651,5.279,49.678,5.215,49.689,5.124,49.721,5.007,49.778,4.931,49.789,4.868,49.788,4.849,49.847,4.842,49.915,4.790,49.960,4.861,50.136,4.819,50.153,4.773,50.139,4.707,50.097,4.675,50.047,4.656,50.002,4.545,49.960,4.369,49.945,4.176,49.960,4.149,49.972,4.137,49.984,4.137,50.000,4.150,50.024,4.184,50.053,4.192,50.094,4.158,50.130,4.135,50.144,4.144,50.178,4.170,50.222,4.175,50.246,4.044,50.321,3.950,50.336,3.789,50.347,3.748,50.344,3.689,50.306,3.667,50.325,3.627,50.457,3.595,50.477,3.477,50.499,3.316,50.507,3.273,50.532,3.250,50.591,3.235,50.663,3.182,50.732,3.107,50.779,3.023,50.767,2.922,50.727,2.840,50.712,2.759,50.751,2.669,50.811,2.597,50.876,2.579,50.912,2.601,50.955,2.536,51.050,2.525,51.097,2.960,51.265,3.225,51.352,3.350,51.378,3.380,51.291,3.403,51.264,3.433,51.246,3.472,51.242,3.580,51.286,3.682,51.276,3.756,51.255,3.782,51.233,3.831,51.213,3.902,51.208,4.040,51.247,4.173,51.307,4.211,51.349,4.226,51.386]],
};
